import React from "react";
import { observer } from "mobx-react";
import { ReviewForm } from "./ReviewForm";
import { SavedStage } from "./SavedStage";
import { CapturedStage } from "./CapturedStage";
import { CopilotModalShell } from "../../CopilotModalShell";

const PHASE_LABELS = [
  ["uploading",    "Uploading recording bundle"],
  ["transcribing", "Transcribing narration (Whisper)"],
  ["sampling",     "Picking the best frames"],
  ["compiling",    "Compiling with the model"],
  ["parsing",      "Parsing playbook"],
];

const PHASE_INDEX = (key) => PHASE_LABELS.findIndex(([k]) => k === key);

// `reviewing` + `saved` stages render a placeholder here in Task 16. Task 17
// replaces the reviewing branch with <ReviewForm/>; Task 18 replaces the
// saved branch with <SavedStage/>. Splitting the imports across tasks keeps
// every intermediate commit's tests green.
export const RecordingWizard = observer(function RecordingWizard({ controller, onRunWorkflow }) {
  const title =
    controller.stage === "captured"  ? "Review recording" :
    controller.stage === "compiling" ? "Compiling workflow…" :
    controller.stage === "reviewing" ? "Review & save workflow" :
    "Workflow saved";

  return (
    <CopilotModalShell
      title={title}
      onClose={() => controller.closeWizard()}
      // The captured + review stages hold a recording / unsaved edits — don't
      // let a stray backdrop click drop them. Close is explicit (× or Escape).
      closeOnBackdrop={controller.stage !== "reviewing" && controller.stage !== "captured"}
    >
      {controller.stage === "captured" && (
        <CapturedStage controller={controller} />
      )}
      {controller.stage === "compiling" && (
        <CompileStage controller={controller} />
      )}
      {controller.stage === "reviewing" && (
        <ReviewForm
          playbook={controller.playbook}
          error={controller.error}
          onSave={(d) => controller.save(d)}
          onRecompile={() => controller.recompile()}
          onDiscard={() =>
            Promise.resolve(controller.discardDraft(controller.draftId)).then(() =>
              controller.closeWizard(),
            )
          }
        />
      )}
      {controller.stage === "saved" && (
        <SavedStage controller={controller} onRunWorkflow={onRunWorkflow} />
      )}
    </CopilotModalShell>
  );
});

const CompileStage = observer(function CompileStage({ controller }) {
  const err = controller.error;
  const currentIndex = PHASE_INDEX(controller.phase);
  return (
    <div className="cua-wizard-body cua-compile-stage">
      {controller.autoStoppedReason === "hard_cap" && (
        <div className="cua-compile-autostop-banner" role="status">
          Recording was auto-stopped at the 15-minute cap. Compile is proceeding with what we captured.
        </div>
      )}
      {!err && <div className="cua-compile-spinner" aria-hidden="true" />}
      <ol className="cua-compile-phases">
        {PHASE_LABELS.map(([key, label], i) => {
          const active = controller.phase === key;
          const reached = currentIndex >= 0 && currentIndex >= i;
          const state = active ? "active" : reached ? "done" : "pending";
          return (
            <li key={key} className={state}>
              {active ? "⟳" : reached ? "✓" : "○"} {label}
            </li>
          );
        })}
      </ol>
      {err && (
        <div className="cua-compile-error" role="alert">
          <div>{err.message || err.code}</div>
          {err.retryable && (
            <button
              type="button"
              className="cua-compile-retry"
              onClick={() => controller.recompile()}
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
});
